import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CalendarDays, User, Globe, CircleDot, MessageCircle, Eye, ChevronDown, ChevronUp, Reply, Trash2, MessagesSquare, Pencil, Upload, Undo, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { usePressRelease, usePressReleaseCommentsWithReplies, useDeletePressRelease, useUpdatePressReleaseStatus, useDeletePressReleaseComment, useDeletePressReleaseReply, useReplyToPressReleaseComment } from '@/lib/api/hooks/press-release';
import { ActionDropdown, DeleteModal, PublishModal, UnpublishModal, type DropdownAction, Pagination } from '@/components/ui';
import ArticleContent from '@/components/ui/ArticleContent';
import { showToast } from '@/lib/toast';

const ITEMS_PER_PAGE = 5;

interface PressReleaseReply {
  id: string;
  responderName: string;
  responderEmail: string;
  response: string;
  dateReplied: string;
}

interface PressReleaseComment {
  id: string;
  name: string;
  email: string;
  comment: string;
  dateCommented: string;
}

interface CommentWithReplies {
  pressReleaseCommentResponse: PressReleaseComment;
  pressReleaseReplyResponses?: PressReleaseReply[];
}

interface CommentWrapper {
  pressReleaseReplyResponseDetails?: CommentWithReplies[];
}

const PressReleaseDetailPage: React.FC = () => {
  const { id: pressReleaseId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [deleteCommentId, setDeleteCommentId] = useState<string | null>(null);
  const [deleteReplyId, setDeleteReplyId] = useState<string | null>(null);
  const [replyingToCommentId, setReplyingToCommentId] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isCommentsCollapsed, setIsCommentsCollapsed] = useState(false);
  const [selectedAdditionalImageIndex, setSelectedAdditionalImageIndex] = useState<number | null>(null);


  const { data: pressReleaseData, isLoading, error } = usePressRelease(pressReleaseId || '');
  const article = pressReleaseData?.data;

  const {
    data: commentsData,
    isLoading: isLoadingComments,
  } = usePressReleaseCommentsWithReplies(pressReleaseId || '');

  const { mutate: deletePressRelease, isPending: isDeleting } = useDeletePressRelease();
  const { mutate: updateStatus, isPending: isUpdatingStatus } = useUpdatePressReleaseStatus();
  const { mutate: deleteComment, isPending: isDeletingComment } = useDeletePressReleaseComment();
  const { mutate: deleteReply, isPending: isDeletingReply } = useDeletePressReleaseReply();
  const { mutate: addReply, isPending: isAddingReply } = useReplyToPressReleaseComment();



  // Extract comments with replies from the API structure
  const rawCommentData = (commentsData?.data ?? []) as CommentWrapper[];
  const commentsWithReplies: CommentWithReplies[] = rawCommentData.flatMap((wrapper) =>
    wrapper.pressReleaseReplyResponseDetails || []
  );

  const totalComments = commentsWithReplies.reduce((acc: number, item: CommentWithReplies) => acc + 1 + (item.pressReleaseReplyResponses?.length || 0), 0);
  const totalPages = Math.ceil(commentsWithReplies.length / ITEMS_PER_PAGE);

  const paginatedComments = commentsWithReplies.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );



  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const day = date.getDate();
      const month = date.toLocaleDateString('en-US', { month: 'short' });
      const hours = date.getHours();
      const minutes = date.getMinutes();
      const ampm = hours >= 12 ? 'pm' : 'am';
      const displayHours = hours % 12 || 12;
      const displayMinutes = minutes.toString().padStart(2, '0');
      return `${day} ${month} at ${displayHours}:${displayMinutes}${ampm}`;
    } catch {
      return dateString;
    }
  };


  const handleEdit = () => {
    navigate(`/admin/press-release/${pressReleaseId}/edit`);
  };

  const handleDeleteClick = () => {
    setDeleteModalOpen(true);
  };

  const handleStatusUpdateClick = () => {
    if (!article) return;
    setStatusModalOpen(true);
  };

  const confirmDelete = () => {
    if (!pressReleaseId) return;
    deletePressRelease(pressReleaseId, {
      onSuccess: (response) => {
        if (response.success) {
          showToast.success('Press release deleted successfully!');
          setDeleteModalOpen(false);
          navigate('/admin/press-release');
        }
      },
      onError: (error: Error) => {
        showToast.error(error);
      }
    });
  };

  const confirmStatusUpdate = () => {
    if (!article || !pressReleaseId) return;
    const newStatus = article.status === 'PUBLISH' ? 'DRAFT' : 'PUBLISH';

    updateStatus(
      { pressReleaseId, status: newStatus },
      {
        onSuccess: (response) => {
          if (response.success) {
            showToast.success(
              newStatus === 'PUBLISH'
                ? 'Press release published successfully!'
                : 'Press release unpublished successfully!'
            );
            setStatusModalOpen(false);
          }
        },
        onError: (error: Error) => {
          showToast.error(error);
        }
      }
    );
  };

  const handleReplySubmit = (commentId: string) => {
    if (!replyText.trim()) return;

    addReply(
      { commentId, reply: replyText },
      {
        onSuccess: () => {
          showToast.success('Reply added successfully!');
          setReplyText('');
          setReplyingToCommentId(null);
        },
        onError: (error: Error) => {
          showToast.error(error);
        }
      }
    );
  };

  const getDropdownActions = (): DropdownAction[] => [
    {
      label: 'Edit',
      onClick: (e) => {
        e.stopPropagation();
        handleEdit();
      }
    },
    {
      label: article?.status === 'PUBLISH' ? 'Un-Publish' : 'Publish',
      onClick: (e) => {
        e.stopPropagation();
        handleStatusUpdateClick();
      },
      disabled: isUpdatingStatus
    },
    {
      label: 'Delete',
      onClick: (e) => {
        e.stopPropagation();
        handleDeleteClick();
      },
      variant: 'danger' as const,
      disabled: isDeleting
    }
  ];

  const quickActions = [
    {
      key: 'edit',
      label: 'Edit',
      icon: Pencil,
      onClick: handleEdit,
      className: 'text-gray-700 border-gray-200 hover:bg-gray-50',
      disabled: false,
    },
    {
      key: 'status',
      label: article?.status === 'PUBLISH' ? 'Un-Publish' : 'Publish',
      icon: article?.status === 'PUBLISH' ? Undo : Upload,
      onClick: handleStatusUpdateClick,
      className: 'text-gray-700 border-gray-200 hover:bg-gray-50',
      disabled: isUpdatingStatus,
    },
    {
      key: 'delete',
      label: 'Delete',
      icon: Trash2,
      onClick: handleDeleteClick,
      className: 'text-red-600 border-red-200 hover:bg-red-50',
      disabled: isDeleting,
    },
  ];

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-10">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-wine-100"></div>
          <p className="mt-4 text-sm text-gray-500">Loading press release...</p>
        </div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="text-center p-8 bg-white rounded-lg">
        <h2 className="text-xl font-bold">Press Release not found</h2>
        <p className="text-sm text-gray-500 mt-2">{error?.message || 'The press release could not be found'}</p>
        <button onClick={() => navigate('/admin/press-release')} className="mt-4 text-[#681628] hover:underline">
          &larr; Back to Press Releases
        </button>
      </div>
    );
  }


  const isUnpublishing = article?.status === 'PUBLISH';
  const additionalImageUrls = Array.isArray((article as unknown as { additionalImageUrls?: string[] }).additionalImageUrls)
    ? (article as unknown as { additionalImageUrls?: string[] }).additionalImageUrls || []
    : [];
  const additionalFileUrls = Array.isArray((article as unknown as { additionalFileUrls?: string[] }).additionalFileUrls)
    ? (article as unknown as { additionalFileUrls?: string[] }).additionalFileUrls || []
    : [];

  const authorName = article.createdByUsername || (article as unknown as { publishedByName?: string }).publishedByName  || article.createdBy;
  const publishedOn = article.datePublished || article.createdAt;
  const lastUpdated = (article as unknown as { updatedAt?: string }).updatedAt || article.createdAt;

  const openAdditionalImage = (index: number) => setSelectedAdditionalImageIndex(index);
  const closeAdditionalImage = () => setSelectedAdditionalImageIndex(null);
  const showPrevAdditionalImage = () => {
    if (!additionalImageUrls.length || selectedAdditionalImageIndex === null) return;
    const nextIndex = (selectedAdditionalImageIndex - 1 + additionalImageUrls.length) % additionalImageUrls.length;
    setSelectedAdditionalImageIndex(nextIndex);
  };
  const showNextAdditionalImage = () => {
    if (!additionalImageUrls.length || selectedAdditionalImageIndex === null) return;
    const nextIndex = (selectedAdditionalImageIndex + 1) % additionalImageUrls.length;
    setSelectedAdditionalImageIndex(nextIndex);
  };

  return (
    <>
      {/* Delete Modal */}
      <DeleteModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        title="Delete Press Release?"
        subtitle="Are you sure you want to delete this press release?"
        itemName={article?.title}
        warningText="This action cannot be undone. The press release will be permanently deleted."
        itemType="Press Release"
        isLoading={isDeleting}
      />

      {/* Publish/Unpublish Modal */}
      {isUnpublishing ? (
        <UnpublishModal
          isOpen={statusModalOpen}
          onClose={() => setStatusModalOpen(false)}
          onConfirm={confirmStatusUpdate}
          itemType="Press Release"
          isLoading={isUpdatingStatus}
        />
      ) : (
        <PublishModal
          isOpen={statusModalOpen}
          onClose={() => setStatusModalOpen(false)}
          onConfirm={confirmStatusUpdate}
          title="Publish Press Release?"
          subtitle="Are you sure you want to publish this press release?"
          itemName={article?.title}
          infoText="It will be displayed on your website."
          itemType="Press Release"
          isLoading={isUpdatingStatus}
        />
      )}

      <div className="space-y-6">
        <div className="text-sm text-gray-500">
          <span className="cursor-pointer hover:underline" onClick={() => navigate('/admin/press-release')}>Press Release</span>
          <span className="mx-2">&gt;</span>
          <span className="font-medium text-gray-700">{article.title}</span>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_360px] gap-6 items-start">
          <div className="space-y-6">
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
              <div className="flex flex-col md:flex-row md:items-start gap-6">
                {article.coverImage && (
                  <img src={article.coverImage} alt={article.title} className="w-full md:w-60 h-44 object-cover rounded-xl flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3">
                    <h1 className="text-3xl font-semibold text-gray-900">{article.title}</h1>
                    <ActionDropdown id={pressReleaseId || ''} actions={getDropdownActions()} />
                  </div>

                  <div className="flex flex-wrap items-center gap-3 mt-4 text-sm text-gray-600">
                    <span className="inline-flex items-center gap-1.5"><CalendarDays className="w-4 h-4" />{formatDate(publishedOn)}</span>
                    <span className="text-gray-300">|</span>
                    <span className="inline-flex items-center gap-1.5"><User className="w-4 h-4" />By {authorName}</span>
                    <span className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-medium ${article.status === 'PUBLISH' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                      <span className={`h-2 w-2 rounded-full ${article.status === 'PUBLISH' ? 'bg-emerald-500' : 'bg-amber-400'}`} />
                      {article.status === 'PUBLISH' ? 'Published' : 'Draft'}
                    </span>
                  </div>

                  <div className="flex flex-wrap items-center gap-3 mt-4 text-sm text-gray-600">
                    <span className="inline-flex items-center gap-1.5"><Globe className="w-4 h-4" />Language: <span className="font-medium text-gray-800">English (EN)</span></span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4">
               <h3 className="text-lg font-semibold text-gray-900">Content</h3>
              <ArticleContent content={article.content || ''} className="" />
            </div>

            {(additionalImageUrls.length > 0 || additionalFileUrls.length > 0) && (
              <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5 space-y-5">
                <h3 className="text-lg font-semibold text-gray-900">Additional Assets</h3>

                {additionalImageUrls.length > 0 && (
                  <div className="space-y-3">
                    <h4 className="text-sm font-semibold text-gray-700">Additional Images</h4>
                    <div className="flex gap-3 overflow-x-auto pb-2">
                      {additionalImageUrls.map((url, index) => (
                        <button
                          key={`${url}-${index}`}
                          type="button"
                          onClick={() => openAdditionalImage(index)}
                          className="shrink-0 rounded-lg border border-gray-200 overflow-hidden hover:border-wine-100 transition-colors"
                          aria-label={`Open additional image ${index + 1}`}
                        >
                          <img src={url} alt={`Additional image ${index + 1}`} className="w-36 h-24 object-cover" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {additionalFileUrls.length > 0 && (
                  <div className="space-y-3">
                    <h4 className="text-sm font-semibold text-gray-700">Additional Files</h4>
                    <ul className="space-y-2">
                      {additionalFileUrls.map((url, index) => (
                        <li key={`${url}-${index}`}>
                          <a
                            href={url}
                            target="_blank"
                            rel="noreferrer noopener"
                            className="text-sm text-wine-100 hover:text-wine-200 hover:underline break-all"
                          >
                            {url}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Comments Section */}
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm m-12 xl:m-0">
          <div className="p-4 flex justify-between items-center border-b border-gray-200 bg-gray-50 rounded-t-lg">
            <h3 className="text-lg flex items-center  text-gray-800 gap-2"><MessagesSquare className="w-5 h-5" /> Comments</h3>
            <button
              type="button"
              onClick={() => setIsCommentsCollapsed((prev) => !prev)}
              className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900"
              aria-expanded={!isCommentsCollapsed}
              aria-label={isCommentsCollapsed ? 'Expand comments section' : 'Collapse comments section'}
            >
              {isCommentsCollapsed ? 'Expand' : 'Collapse'}
              {isCommentsCollapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
            </button>
          </div>
          {!isCommentsCollapsed && (
          <div className="space-y-4 p-6">
            {isLoadingComments ? (
              <div className="text-center py-8 text-gray-500">
                <p className="text-sm">Loading comments...</p>
              </div>
            ) : commentsWithReplies.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p className="text-sm">No comments yet</p>
              </div>
            ) : (
              <>
                {paginatedComments.map((item: CommentWithReplies) => {
                  const comment = item.pressReleaseCommentResponse;
                  const replies = item.pressReleaseReplyResponses || [];

                  return (
                    <div key={comment.id} className="space-y-3">
                      {/* Main Comment */}
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <div className="flex justify-between items-start gap-3">
                          <div>
                            <p className="font-semibold text-gray-800">{comment.name}</p>
                            <p className="text-xs text-gray-500">{comment.email}</p>
                          </div>
                          <div className="flex items-center gap-2 flex-wrap justify-end">
                            <button
                              type="button"
                              onClick={() => {
                                setReplyingToCommentId(comment.id);
                                setReplyText('');
                              }}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-md hover:bg-gray-100"
                            >
                              <Reply className="w-3.5 h-3.5" />
                              Reply
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setDeleteCommentId(comment.id);
                              }}
                              disabled={isDeletingComment}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 bg-white border border-red-200 rounded-md hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                              Delete Comment
                            </button>
                          </div>
                        </div>
                        <p className="text-sm text-gray-600 mt-2">{comment.comment}</p>
                        <p className="text-xs text-gray-400 mt-2">{formatDate(comment.dateCommented)}</p>

                        {/* Reply Input */}
                        {replyingToCommentId === comment.id && (
                          <div className="mt-4 flex gap-2">
                            <input
                              type="text"
                              value={replyText}
                              onChange={(e) => setReplyText(e.target.value)}
                              placeholder="Write a reply..."
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-wine-100"
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  handleReplySubmit(comment.id);
                                }
                              }}
                            />
                            <button
                              onClick={() => handleReplySubmit(comment.id)}
                              disabled={isAddingReply || !replyText.trim()}
                              className="px-4 py-2 bg-wine-100 text-white rounded-md text-sm font-medium hover:bg-wine-200 disabled:opacity-50"
                            >
                              {isAddingReply ? 'Sending...' : 'Reply'}
                            </button>
                            <button
                              onClick={() => setReplyingToCommentId(null)}
                              className="px-3 py-2 text-gray-500 hover:text-gray-700 text-sm"
                            >
                              Cancel
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Replies */}
                      {replies.length > 0 && (
                        <div className="ml-8 space-y-2">
                          {replies.map((reply: PressReleaseReply) => (
                            <div key={reply.id} className="p-3 bg-white border border-gray-200 rounded-lg">
                              <div className="flex justify-between items-start">
                                <div>
                                  <p className="font-medium text-sm text-gray-800">{reply.responderName}</p>
                                  <p className="text-xs text-gray-500">{reply.responderEmail}</p>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setDeleteReplyId(reply.id);
                                  }}
                                  disabled={isDeletingReply}
                                  className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-red-600 bg-white border border-red-200 rounded-md hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                  Delete Reply
                                </button>
                              </div>
                              <p className="text-sm text-gray-600 mt-2">{reply.response}</p>
                              <p className="text-xs text-gray-400 mt-1">{formatDate(reply.dateReplied)}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
                {totalPages > 1 && (
                  <div className="mt-6 pt-4 border-t border-gray-100">
                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={setCurrentPage}
                    />
                  </div>
                )}
              </>
            )}
          </div>
          )}
        </div>
          </div>

          <aside className="xl:sticky xl:top-24 space-y-4">
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5">
              <h3 className="text-lg font-semibold text-gray-900">Press Release Details</h3>

              <div className="mt-4 space-y-3 text-sm">
                <div className="flex justify-between gap-3 items-start">
                  <span className="text-gray-500 inline-flex items-center gap-2"><User className="w-4 h-4" />Author</span>
                  <span className="text-gray-900 font-medium text-right">{authorName}</span>
                </div>
                <div className="flex justify-between gap-3 items-start">
                  <span className="text-gray-500 inline-flex items-center gap-2"><CalendarDays className="w-4 h-4" />Published On</span>
                  <span className="text-gray-900 font-medium text-right">{formatDate(publishedOn)}</span>
                </div>
                <div className="flex justify-between gap-3 items-start">
                  <span className="text-gray-500 inline-flex items-center gap-2"><CalendarDays className="w-4 h-4" />Last Updated</span>
                  <span className="text-gray-900 font-medium text-right">{formatDate(lastUpdated)}</span>
                </div>
                <div className="flex justify-between gap-3 items-start">
                  <span className="text-gray-500 inline-flex items-center gap-2"><Globe className="w-4 h-4" />Language</span>
                  <span className="text-gray-900 font-medium text-right">English (EN)</span>
                </div>
                <div className="flex justify-between gap-3 items-start">
                  <span className="text-gray-500 inline-flex items-center gap-2"><CircleDot className="w-4 h-4" />Status</span>
                  <span className={`inline-flex items-center gap-1 text-xs font-medium ${article.status === 'PUBLISH' ? 'text-emerald-700' : 'text-amber-700'}`}>
                    <span className={`h-2 w-2 rounded-full ${article.status === 'PUBLISH' ? 'bg-emerald-500' : 'bg-amber-400'}`} />
                    {article.status === 'PUBLISH' ? 'Published' : 'Draft'}
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5">
              <h3 className="text-lg font-semibold text-gray-900">Engagement Overview</h3>
              <div className="grid grid-cols-2 gap-3 mt-4">
                <div className="rounded-lg border border-gray-200 p-3">
                  <p className="text-xs text-gray-500 inline-flex items-center gap-1.5"><Eye className="w-3.5 h-3.5" />Views</p>
                  <p className="text-xl font-semibold text-gray-900 mt-1">{(article as unknown as { totalViews?: number }).totalViews || 0}</p>
                </div>
                <div className="rounded-lg border border-gray-200 p-3">
                  <p className="text-xs text-gray-500 inline-flex items-center gap-1.5"><MessageCircle className="w-3.5 h-3.5" />Comments</p>
                  <p className="text-xl font-semibold text-gray-900 mt-1">{totalComments}</p>
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5">
              <h3 className="text-lg font-semibold text-gray-900">Quick Actions</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                {quickActions.map((action) => {
                  const ActionIcon = action.icon;

                  return (
                    <button
                      key={action.key}
                      type="button"
                      onClick={action.onClick}
                      disabled={action.disabled}
                      className={`inline-flex items-center justify-center gap-2.5 px-4 py-3 rounded-lg border text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${action.className}`}
                    >
                      <ActionIcon className="w-4 h-4" />
                      {action.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </aside>
        </div>
      </div>

      {selectedAdditionalImageIndex !== null && additionalImageUrls.length > 0 && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <button
            type="button"
            onClick={closeAdditionalImage}
            className="absolute top-4 right-4 p-2 rounded-full bg-white/20 text-white hover:bg-white/30"
            aria-label="Close image viewer"
          >
            <X className="w-5 h-5" />
          </button>

          <button
            type="button"
            onClick={showPrevAdditionalImage}
            className="absolute left-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/20 text-white hover:bg-white/30"
            aria-label="Previous image"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>

          <img
            src={additionalImageUrls[selectedAdditionalImageIndex]}
            alt={`Additional image ${selectedAdditionalImageIndex + 1}`}
            className="max-h-[85vh] max-w-[90vw] object-contain rounded-lg"
          />

          <button
            type="button"
            onClick={showNextAdditionalImage}
            className="absolute right-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/20 text-white hover:bg-white/30"
            aria-label="Next image"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </div>
      )}

      {/* Delete Comment Modal */}
      {deleteCommentId && (
        <DeleteModal
          isOpen={true}
          onClose={() => setDeleteCommentId(null)}
          onConfirm={() => {
            deleteComment(deleteCommentId, {
              onSuccess: () => {
                showToast.success('Comment deleted successfully!');
                setDeleteCommentId(null);
              },
              onError: (error: Error) => {
                showToast.error(error);
              }
            });
          }}
          title="Delete Comment"
          subtitle="Are you sure you want to delete this comment and all its replies?"
          warningText="This action cannot be undone."
          isLoading={isDeletingComment}
          itemType="Comment"
        />
      )}

      {/* Delete Reply Modal */}
      {deleteReplyId && (
        <DeleteModal
          isOpen={true}
          onClose={() => setDeleteReplyId(null)}
          onConfirm={() => {
            deleteReply(deleteReplyId, {
              onSuccess: () => {
                showToast.success('Reply deleted successfully!');
                setDeleteReplyId(null);
              },
              onError: (error: Error) => {
                showToast.error(error);
              }
            });
          }}
          title="Delete Reply"
          subtitle="Are you sure you want to delete this reply?"
          warningText="This action cannot be undone."
          isLoading={isDeletingReply}
          itemType="Reply"
        />
      )}
    </>
  );
};

export default PressReleaseDetailPage;
